import { ErrorCode } from '@approvalmax/types';
import { errorHelpers, intl, miscHelpers } from '@approvalmax/utils';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce, useUnmount } from 'react-use';
import { t } from 'services/localization';
import { CreateConversionQuote, CurrencyIsoCode, FixedSide, useCreateConversionQuote } from 'shared/data/wallets/v1';
import { getCountryCodeByCurrency, getCountryFlagByCountryCode } from 'shared/helpers/(wallets)';
import { useCompanyIdBasedOnUrl } from 'shared/hooks';
import { WalletAvailableCurrency } from 'shared/hooks/(wallets)';

export const useCurrenciesInfo = (
    fromCurrency: CurrencyIsoCode | undefined,
    toCurrency: CurrencyIsoCode | undefined,
    currencies: WalletAvailableCurrency[] | undefined
) => {
    const { IconFromFlag, IconToFlag } = useMemo(() => {
        const IconFromFlag = fromCurrency
            ? getCountryFlagByCountryCode(getCountryCodeByCurrency(fromCurrency))
            : undefined;
        const IconToFlag = toCurrency ? getCountryFlagByCountryCode(getCountryCodeByCurrency(toCurrency)) : undefined;

        return {
            IconFromFlag,
            IconToFlag,
        };
    }, [fromCurrency, toCurrency]);

    const { fromBalance, toBalance } = useMemo(() => {
        const fromBalance = currencies?.find((item) => item.id === fromCurrency)?.balance;
        const toBalance = currencies?.find((item) => item.id === toCurrency)?.balance;

        return {
            fromBalance: fromBalance ?? 0,
            toBalance: toBalance ?? 0,
        };
    }, [fromCurrency, toCurrency, currencies]);

    return {
        IconFromFlag,
        IconToFlag,
        fromBalance,
        toBalance,
    };
};

export const useConversionRate = (
    fromCurrency: CurrencyIsoCode,
    toCurrency: CurrencyIsoCode,
    companyId: string | undefined
) => {
    const { companyId: companyIdFromUrl } = useCompanyIdBasedOnUrl();

    const [rate, setRate] = useState<{
        fromCurrency: CurrencyIsoCode;
        toCurrency: CurrencyIsoCode;
        value: number;
    } | null>(null);
    const [rateDate, setRateDate] = useState<string | null>(null);
    const [quoteOptions, setQuoteOptions] = useState<CreateConversionQuote['body'] | null>(null);

    const [, cancel] = useDebounce(
        () => {
            setRate(null);

            if (!fromCurrency || !toCurrency) {
                setQuoteOptions(null);

                return;
            }

            setQuoteOptions({
                buyCurrency: toCurrency,
                sellCurrency: fromCurrency,
                // as we just need to get a conversion rate, we can set any amount here
                amount: 100,
                fixedSide: FixedSide.sell,
            });
        },
        500,
        [fromCurrency, toCurrency]
    );

    const {
        mutate: createConversionQuote,
        mutateAsync: createConversionQuoteAsync,
        isLoading: isLoadingCreateConversionQuote,
        reset: resetCreateConversionQuote,
    } = useCreateConversionQuote(undefined, {
        skipToastForErrorCodes: [ErrorCode.ConversionAmountOverLimit, ErrorCode.ConversionAmountUnderLimit],
    });

    useUnmount(() => {
        cancel();
    });

    const loadRate = useCallback(() => {
        if (!quoteOptions) {
            return;
        }

        resetCreateConversionQuote();

        createConversionQuote(
            {
                params: {
                    path: {
                        organizationId: companyId ?? companyIdFromUrl,
                    },
                },
                body: quoteOptions,
            },
            {
                onSuccess: (data) => {
                    const firstCurrency = miscHelpers.castToEnum(CurrencyIsoCode, data.currencyPair.slice(0, 3));
                    const secondCurrency = miscHelpers.castToEnum(CurrencyIsoCode, data.currencyPair.slice(3));

                    if (!firstCurrency || !secondCurrency) {
                        return;
                    }

                    setRate({
                        fromCurrency: firstCurrency,
                        toCurrency: secondCurrency,
                        value: data.clientRate,
                    });

                    setRateDate(moment().format('DD MMM YYYY HH:mm'));
                },
            }
        );
    }, [companyId, companyIdFromUrl, createConversionQuote, quoteOptions, resetCreateConversionQuote]);

    const clearRate = useCallback(() => {
        setRate(null);
        setRateDate(null);
    }, []);

    useEffect(() => {
        loadRate();
    }, [loadRate]);

    const checkConversionRate = useCallback(
        async (conversionQuoteParams: CreateConversionQuote['body']) => {
            try {
                await createConversionQuoteAsync({
                    params: {
                        path: {
                            organizationId: companyId ?? companyIdFromUrl,
                        },
                    },
                    body: conversionQuoteParams,
                });
            } catch (error) {
                if (errorHelpers.isApiError(error) && error.error) {
                    const { currency, limit, baseCurrency, baseLimit } = error.error;

                    const formattedLimit = intl.formatNumber(limit, 'inherit');
                    const formattedBaseLimit = intl.formatNumber(baseLimit, 'inherit');

                    const amountMessage =
                        currency === baseCurrency
                            ? t('var_amount_var_currency', {
                                  amount: formattedLimit,
                                  currency,
                              })
                            : t('amount_currency_baseamount_basecurrency', {
                                  amount: formattedLimit,
                                  currency,
                                  baseAmount: formattedBaseLimit,
                                  baseCurrency: baseCurrency,
                              });

                    switch (error.code) {
                        case ErrorCode.ConversionAmountOverLimit:
                            return t('conversion_amount_should_not_exceed_available_limit_of_var_amount', {
                                amount: amountMessage,
                            });
                        case ErrorCode.ConversionAmountUnderLimit:
                            return t('conversion_amount_should_not_be_less_than_available_limit_of_var_amount', {
                                amount: amountMessage,
                            });
                    }
                }
            }

            return null;
        },
        [companyId, companyIdFromUrl, createConversionQuoteAsync]
    );

    return {
        rate,
        rateDate,
        isLoading: isLoadingCreateConversionQuote,
        loadRate,
        clearRate,
        checkConversionRate,
    };
};
